import { GlobalMessages } from '@shared/localization/messages';
import { useMemo, useState } from 'react';

import CheckCircleIcon from '@/assets/check-circle-icon.svg?react';
import { Button } from '@/components/atoms/Button';
import FieldErrors from '@/components/atoms/FieldErrors';
import { WysiwygTextField } from '@/components/atoms/WysiwygTextField';
import type { ModalStatelessProps } from '@/components/ModalStateless';
import { ModalStateless } from '@/components/ModalStateless';
import { entityFieldSettings } from '@/constants/entityFieldSettings';
import { stripHtml } from '@/helpers/estimation';
import { useMessages } from '@/hooks/messages';

import { estimationDetailEstimationItemOmitModalMessageDefinitions } from './messages';

type OmitModalProps = Omit<ModalStatelessProps, 'title'> & {
  reason?: string;
  onConfirm: (reason: string) => void;
};

export const OmitModal: React.FC<OmitModalProps> = ({ open, onClose, onConfirm, reason }) => {
  const globalMessages = useMessages(GlobalMessages);
  const messages = useMessages(estimationDetailEstimationItemOmitModalMessageDefinitions);

  const [value, setValue] = useState<string>(reason ?? '');

  const reasonTooLong = useMemo(
    () =>
      value.length > entityFieldSettings.estimationItemEntity.omitReasonMaxLength ||
      stripHtml(value).length >
        entityFieldSettings.estimationItemEntity.omitReasonStrippedHtmlMaxLength,
    [value],
  );

  return (
    <ModalStateless
      title={messages.title()}
      open={open}
      onClose={onClose}
      buttons={
        <>
          <Button variant='secondary' outlined onClick={onClose}>
            {globalMessages.cancel()}
          </Button>
          <Button
            variant='primary'
            IconComponentLeft={CheckCircleIcon}
            disabled={!value || reasonTooLong}
            onClick={() => {
              onConfirm(value);
              setValue('');
            }}
          >
            {messages.confirmButton()}
          </Button>
        </>
      }
    >
      <WysiwygTextField
        value={value}
        onChange={setValue}
        placeholder={messages.reasonPlaceholder()}
      />
      {reasonTooLong && <FieldErrors error={{ type: 'maxLength' }} />}
    </ModalStateless>
  );
};
